// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The writer.
 *
 * A rewrite is expressed as an ordered PLAN of parts, where a part is either a
 * range of the ORIGINAL file or a small run of newly authored bytes. Nothing
 * else. That shape is what makes two promises true at once:
 *
 *   - The image data is untouched. A preserved chunk is copied as a byte range
 *     including its ORIGINAL four CRC bytes, so IDAT is never re-CRC'd and never
 *     re-encoded, and the output's image data compares equal to the input's.
 *   - A 60 MB file never lands on the JS heap. `assemblePlan` hands the Blob
 *     constructor `subarray` views; the bytes are copied into blob storage by
 *     the browser, not accumulated in JavaScript.
 *
 * The measured alternative, for the record: `png-chunks-encode` builds a boxed
 * JS Array with one element per byte and recomputes the CRC of every chunk
 * including IDAT — 367 ms for 1 MB, 27 s for 8 MB, and it did not finish 20 MB
 * inside two minutes.
 */

import { crc32Pair } from './crc32.ts';
import type { PlanPart, PngParse, RewriteOptions } from '../types.ts';

/** A complete chunk: length | type | data | CRC. */
export function chunkBytes(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32Pair(out, 4, 8, data, 0, data.length));
  return out;
}

export const IEND_CHUNK = chunkBytes('IEND', new Uint8Array(0));

export function planRewrite(parse: PngParse, options: RewriteOptions = {}): PlanPart[] {
  const insert = options.insertAfterIhdr ?? [];
  const remove = options.removeIndices ?? new Set<number>();
  const keepTrailer = options.keepTrailer !== false;
  const replaceIdat = options.replaceIdat;

  const parts: PlanPart[] = [{ kind: 'slice', from: 0, to: 8 }];
  const hasIhdr = parse.chunks.some((c) => c.type === 'IHDR');
  let inserted = false;
  let idatEmitted = false;

  // A file with no IHDR is malformed, but it can still carry text chunks worth
  // recovering. Insert straight after the signature rather than dropping the
  // new chunks on the floor.
  if (!hasIhdr && insert.length > 0) {
    for (const data of insert) parts.push({ kind: 'bytes', data });
    inserted = true;
  }

  for (const chunk of parse.chunks) {
    if (remove.has(chunk.index)) continue;

    if (chunk.type === 'IDAT' && replaceIdat) {
      if (!idatEmitted) {
        for (const data of replaceIdat) parts.push({ kind: 'bytes', data });
        idatEmitted = true;
      }
      continue;
    }

    parts.push({ kind: 'slice', from: chunk.offset, to: chunk.dataStart + chunk.dataLen + 4 });

    if (!inserted && chunk.type === 'IHDR') {
      for (const data of insert) parts.push({ kind: 'bytes', data });
      inserted = true;
    }
  }

  // A missing IEND is tolerated on read; it must not be propagated on write, or
  // the output is a file some decoders refuse.
  if (parse.missingIend) parts.push({ kind: 'bytes', data: IEND_CHUNK });

  if (keepTrailer && parse.trailerLength > 0) {
    parts.push({ kind: 'slice', from: parse.trailerStart, to: parse.totalLength });
  }

  return parts;
}

export function planLength(plan: PlanPart[]): number {
  let total = 0;
  for (const part of plan) total += part.kind === 'slice' ? part.to - part.from : part.data.length;
  return total;
}

/** The output as a Blob. Structured clone passes a Blob by reference. */
export function assemblePlan(bytes: Uint8Array, plan: PlanPart[], type = 'image/png'): Blob {
  const parts: BlobPart[] = plan.map((part) =>
    part.kind === 'slice' ? (bytes.subarray(part.from, part.to) as BlobPart) : (part.data as BlobPart),
  );
  return new Blob(parts, { type });
}

/** The output as one buffer. Used by the tests, never on the hot path. */
export function materialisePlan(bytes: Uint8Array, plan: PlanPart[]): Uint8Array {
  const out = new Uint8Array(planLength(plan));
  let at = 0;
  for (const part of plan) {
    if (part.kind === 'slice') {
      out.set(bytes.subarray(part.from, part.to), at);
      at += part.to - part.from;
    } else {
      out.set(part.data, at);
      at += part.data.length;
    }
  }
  return out;
}
